import React, { useEffect, useRef, useState } from "react"
import { Stack } from "@carrotfertility/carotene"
import { HR, Spacer } from "../../atoms/Atoms"
import { RetinaImage } from "../../atoms/images/RetinaImage"
import "css/usermenu.scss"
import { H4 } from "@carrotfertility/carotene"
import { useDispatch, useSelector } from "react-redux"
import { getFirstName, getIsTestUser, getLastName, getPreferredName } from "../../../../reducers/userInfo"
import { useToggle } from "../../../../utils/Hooks"
import { TextLink } from "../../atoms/links/TextLink"
import { useHistory } from "react-router-dom"
import { signOut } from "../../../../actions/loginActions"
import { toggleDymamicContentTogglesTool } from "../../../../actions/testUserActions"
import { useFeatureToggle } from "components/context/feature-toggle/FeatureToggleContext"
import { useBenefitEnrollment } from "components/context/enrollment/BenefitEnrollmentContext"
import { BenefitAvailabilityStatus } from "types/benefitEnrollmentStatus"

const NavItem = ({ id, to = null, onClick = null, text }) => (
  <TextLink underline={false} to={to} onClick={onClick} id={id}>
    <div className="f6 lh-copy dib v-mid no-underline gotham black-80 dim w-100">{text}</div>
  </TextLink>
)

const MenuContent = ({ toggleExpanded }) => {
  const isTestUser = useSelector((state) => getIsTestUser(state))
  const {
    status: { needsBenefitEnrollment },
    availabilityStatus
  } = useBenefitEnrollment()
  const { showMultiYearCsd } = useFeatureToggle()
  const history = useHistory()
  const dispatch = useDispatch()
  const handleSignOutClick = () => {
    dispatch(signOut())
  }

  const handleAccountClick = () => {
    toggleExpanded()
    history.push("/account")
  }

  const handleInsuranceDetailsClick = () => {
    toggleExpanded()
    history.push("/insurance-details")
  }

  const handleDynamicContentTool = () => {
    toggleExpanded()
    dispatch(toggleDymamicContentTogglesTool())
  }

  const displayInsuranceDetails = availabilityStatus !== BenefitAvailabilityStatus.NEEDS_BENEFIT_ENROLLMENT

  return (
    <Stack spacing="small">
      <HR color="black-10" />
      <NavItem id="nav-account-settings-link" onClick={handleAccountClick} text="Account settings" />
      {showMultiYearCsd && displayInsuranceDetails && (
        <NavItem id="nav-insurance-details-link" onClick={handleInsuranceDetailsClick} text="Insurance Details" />
      )}
      {isTestUser && (
        <NavItem id="nav-account-settings-link" onClick={handleDynamicContentTool} text="Dynamic Content Tool" />
      )}
      <NavItem id="nav-sign-out-link" onClick={handleSignOutClick} text="Sign out" />
    </Stack>
  )
}

const DesktopMenu = ({ userName }) => {
  const [expanded, toggleExpanded] = useToggle(false)
  const wrapperRef = useRef(null)
  useOutsideAlerter(wrapperRef, () => toggleExpanded(false))

  return (
    <div className="relative">
      <div className={`absolute w-100`}>
        <div className="flex justify-end">
          <div
            ref={wrapperRef}
            onClick={toggleExpanded}
            className={`mr3 mr0-ns mt5-ns pa3-ns usermenu ${expanded ? "expanded" : null} pointer`}
          >
            <div className="dn db-ns relative">
              <Stack spacing="small">
                <UserMenuButton userName={userName} expanded={expanded} />
                {expanded ? (
                  <div className="relative">
                    <MenuContent toggleExpanded={toggleExpanded} />
                  </div>
                ) : null}
              </Stack>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const MobileMenu = ({ userName }) => {
  const [expanded, toggleExpanded] = useToggle(false)
  const wrapperRef = useRef(null)
  useOutsideAlerter(wrapperRef, () => toggleExpanded(false))

  return (
    <div className="dn-ns">
      {expanded ? (
        <div className="fixed z-9999 left-0 top-0 h-100 w-100 bg-black-50 pa35">
          <div className="bg-white w-100 usermenu-mobile pa3" ref={wrapperRef}>
            <div onClick={toggleExpanded} className="tr mr3 mt3">
              <img alt="close" src="/images/icn-close-modal.svg" />
            </div>
            <Spacer height={1} />
            <UserNameIcon userName={userName} />
            <MenuContent toggleExpanded={toggleExpanded} />
          </div>
        </div>
      ) : (
        <div onClick={toggleExpanded}>
          <RetinaImage height={32} width={32} name="icn-user" />
        </div>
      )}
    </div>
  )
}

const UserNameIcon = ({ userName }) => (
  <div className="flex items-center">
    <RetinaImage height={32} width={32} name="icn-user" />
    <Spacer width={0.5} />
    <H4 variant="primary">{userName}</H4>
  </div>
)

const UserMenuButton = ({ userName, expanded }) => (
  <div className="flex items-center">
    <UserNameIcon userName={userName} />
    <div className="flex-none ml2">
      <img
        className={`animate-all v-mid ${expanded ? "rotate-180" : ""}`}
        alt={expanded ? "close" : "open"}
        src={`/images/icn-disclose-open.svg`}
      />
    </div>
  </div>
)

const UserMenu = ({ className = "" }) => {
  const firstName = useSelector(getFirstName)
  const lastName = useSelector(getLastName)
  const preferredName = useSelector(getPreferredName)
  const [userName, setUserName] = useState("")

  useEffect(() => {
    if (preferredName) {
      if (preferredName.length < 12) setUserName(preferredName)
      else setUserName("Account")
    } else {
      if (firstName.length < 12) setUserName(`${firstName} ${lastName[0]}.`)
      else setUserName("Account")
    }
  }, [preferredName, firstName])

  return (
    <div className={`${className}`}>
      <DesktopMenu userName={userName} />
      <MobileMenu userName={userName} />
    </div>
  )
}

function useOutsideAlerter(ref, callback) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback()
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [ref])
}

export { UserMenu }
